import { ANVIL_ACCOUNTS, generateAccounts } from "@gotts.ai/testnet";
import { encodeFunctionData, parseEther } from "viem";

import {
  MOCK_ERC20_ABI,
  MOCK_ERC20_BYTECODE,
} from "../contracts/tokens/mock-erc20";
import { deployContract } from "../deployer/deploy-utils";

import type { DeployContext } from "../deployer/deploy-utils";
import type { MockTokenAddresses } from "../deployer/types";
import type { Abi, Address, Hex } from "viem";

export interface SeedTokensResult {
  tokens: MockTokenAddresses;
  durationMs: number;
}

/**
 * Token configurations for deployment.
 */
const MOCK_TOKENS = [
  { symbol: "USDC", name: "USD Coin", decimals: 6 },
  { symbol: "USDT", name: "Tether USD", decimals: 6 },
  { symbol: "DAI", name: "Dai Stablecoin", decimals: 18 },
  { symbol: "WBTC", name: "Wrapped BTC", decimals: 8 },
] as const;

/**
 * Tiered token balances for funded accounts.
 * Index 0 (deployer) gets the largest allocation.
 */
const BALANCE_TIERS: readonly bigint[] = [
  10_000_000n, // deployer: 10M units
  1_000_000n, // alice
  500_000n, // bob
  100_000n, // carol
  50_000n, // dave
  10_000n, // eve
];

/**
 * Deploy 4 real MockERC20 tokens and fund accounts with tiered balances.
 *
 * WETH is the already-deployed WETH9 from the foundation phase.
 * USDC, USDT, DAI, WBTC are real MockERC20 contracts with mint().
 */
export async function seedTokens(
  ctx: DeployContext,
  weth9Address: Address,
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedTokensResult> {
  const start = Date.now();

  const artifact = {
    abi: MOCK_ERC20_ABI as Abi,
    bytecode: MOCK_ERC20_BYTECODE as Hex,
  };
  const tokenAddresses: Record<string, Address> = {};

  // Deploy each MockERC20 token
  for (let ti = 0; ti < MOCK_TOKENS.length; ti++) {
    const def = MOCK_TOKENS[ti];
    if (!def) continue;
    await onAction?.(
      `Deploying ${def.symbol} (${String(ti + 1)}/${String(MOCK_TOKENS.length)})`,
    );
    const { address } = await deployContract(
      ctx.publicClient,
      ctx.walletClient,
      ctx.deployer,
      artifact,
      [def.name, def.symbol, def.decimals],
    );
    tokenAddresses[def.symbol.toLowerCase()] = address;
  }

  const tokens: MockTokenAddresses = {
    weth: weth9Address,
    usdc: tokenAddresses.usdc as Address,
    usdt: tokenAddresses.usdt as Address,
    dai: tokenAddresses.dai as Address,
    wbtc: tokenAddresses.wbtc as Address,
  };

  // Mint tokens to funded accounts
  const accountsToFund = ANVIL_ACCOUNTS.slice(0, BALANCE_TIERS.length);
  const totalMints = MOCK_TOKENS.length * accountsToFund.length;
  let mintCount = 0;

  for (const def of MOCK_TOKENS) {
    const tokenAddr = tokenAddresses[def.symbol.toLowerCase()] as Address;
    for (let i = 0; i < accountsToFund.length; i++) {
      const account = accountsToFund[i];
      if (!account) continue;
      const tier = BALANCE_TIERS[i];
      if (tier === undefined) continue;

      mintCount++;
      await onAction?.(
        `Minting ${def.symbol} — account ${String(mintCount)}/${String(totalMints)}`,
      );

      const scaledBalance = tier * 10n ** BigInt(def.decimals);
      const data = encodeFunctionData({
        abi: MOCK_ERC20_ABI,
        functionName: "mint",
        args: [account.address, scaledBalance],
      });

      await ctx.walletClient.sendTransaction({
        account: ctx.deployer,
        to: tokenAddr,
        data: data,
        chain: ctx.walletClient.chain,
      });
    }
  }

  // Fund deployer WETH by depositing ETH into WETH9
  await onAction?.("Wrapping WETH for deployer");
  const depositSelector = "0xd0e30db0" as Hex; // deposit()

  const deployerDepositAmount = parseEther("1000");
  await ctx.walletClient.sendTransaction({
    account: ctx.deployer,
    to: weth9Address,
    data: depositSelector,
    value: deployerDepositAmount,
    chain: ctx.walletClient.chain,
  });

  // Wrap WETH for additional test accounts (#1-9)
  // Accounts #1-4: 200 WETH each, Accounts #5-9: 50 WETH each
  const WETH_TIERS: readonly { start: number; end: number; amount: bigint }[] =
    [
      { start: 1, end: 4, amount: parseEther("200") },
      { start: 5, end: 9, amount: parseEther("50") },
    ];

  const totalWethAccounts = 9; // accounts #1-9
  let wethCount = 0;

  // Ensure accounts #1-9 have sufficient ETH before wrapping
  for (const tier of WETH_TIERS) {
    for (let i = tier.start; i <= tier.end; i++) {
      const account = ANVIL_ACCOUNTS[i];
      if (!account) continue;
      await ctx.request({
        method: "anvil_setBalance",
        params: [account.address, "0x21E19E0C9BAB2400000"], // 10,000 ETH
      });
    }
  }

  for (const tier of WETH_TIERS) {
    for (let i = tier.start; i <= tier.end; i++) {
      const account = ANVIL_ACCOUNTS[i];
      if (!account) continue;

      wethCount++;
      await onAction?.(
        `Wrapping WETH — account ${String(wethCount)}/${String(totalWethAccounts)}`,
      );

      // Impersonate account so we can send tx on their behalf
      await ctx.request({
        method: "anvil_impersonateAccount",
        params: [account.address],
      });

      // Deposit ETH into WETH9
      await ctx.walletClient.sendTransaction({
        account: account.address,
        to: weth9Address,
        data: depositSelector,
        value: tier.amount,
        chain: ctx.walletClient.chain,
      });

      // Stop impersonating
      await ctx.request({
        method: "anvil_stopImpersonatingAccount",
        params: [account.address],
      });
    }
  }

  // Wrap WETH for generated accounts #10-19 (10 WETH each)
  const extraAccounts = generateAccounts(10, 10);
  for (let ei = 0; ei < extraAccounts.length; ei++) {
    const account = extraAccounts[ei];
    if (!account) continue;
    await onAction?.(
      `Funding generated account ${String(ei + 1)}/${String(extraAccounts.length)}`,
    );

    // Fund with 100 ETH
    await ctx.request({
      method: "anvil_setBalance",
      params: [account.address, "0x56BC75E2D63100000"],
    });

    // Impersonate account
    await ctx.request({
      method: "anvil_impersonateAccount",
      params: [account.address],
    });

    // Deposit 10 ETH into WETH9
    await ctx.walletClient.sendTransaction({
      account: account.address,
      to: weth9Address,
      data: depositSelector,
      value: parseEther("10"),
      chain: ctx.walletClient.chain,
    });

    // Stop impersonating
    await ctx.request({
      method: "anvil_stopImpersonatingAccount",
      params: [account.address],
    });
  }

  return {
    tokens,
    durationMs: Date.now() - start,
  };
}
